/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, ICoreContext } from '@msdyn365-commerce/core';
import { ProductDimensionType, ProductDimensionValueInventoryAvailability, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    checkIfShouldDisplayAsSwatch,
    DimensionTypes,
    getInventoryLevelCodeFromDimensionValue,
    IDimensionsApp,
    InventoryLevelValues,
    getDimensionAvailabilitiesForProductsAction,
    ProductsDimensionAvailabilitiesInput
} from '@msdyn365-commerce-modules/retail-actions';

/**
 * CGetDimensionAvailabilitiesForProductSearchResult Input Action
 */
export class CGetDimensionAvailabilitiesForProductSearchResultInput implements IActionInput {
    public products: ProductSearchResult[];

    public dimensionContext: ICoreContext<IDimensionsApp>;
    // TODO: Construct the input needed to run the action
    constructor(products: ProductSearchResult[], dimensionContext: ICoreContext<IDimensionsApp>) {
        this.products = products;
        this.dimensionContext = dimensionContext;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    /**
     * Get the cache key.
     * @returns The cache key.
     */
    public getCacheKey = (): string => 'DimensionAvailabilitiesForProductSearchResult';

    /**
     * Get the cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'ProductDimensionValueInventoryAvailability';

    /**
     * Define the data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetDimensionAvailabilitiesForProductSearchResultDataAction.
 * @param products -- The product search result.
 * @param dimensionContext -- The dimension app context.
 * @returns The input.
 */

// TODO: Create a data model here or import one to capture the response of the action
export interface ICGetDimensionAvailabilitiesForProductSearchResultData {
    text: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */

export const createDimensionAvailabilitiesForProductSearchResultInput = (
    products: ProductSearchResult[],
    dimensionContext: ICoreContext<IDimensionsApp>
): CGetDimensionAvailabilitiesForProductSearchResultInput => {
    return new CGetDimensionAvailabilitiesForProductSearchResultInput(products, dimensionContext);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
export interface ICProductsDimensionsAvailabilities {
    value: string;
    isDisabled?: boolean;
    masterProductId?: number;
    NB?: boolean;
    typeCode?: number;
}
export async function cgetDimensionAvailabilitiesForProductSearchResultAction(
    input: CGetDimensionAvailabilitiesForProductSearchResultInput,
    context: IActionContext
): Promise<ICProductsDimensionsAvailabilities[][]> {
    const products = input.products;
    const productsDimensionInputs: ProductsDimensionAvailabilitiesInput[] = [];
    const inventoryLevel = input.dimensionContext.app.config.inventoryLevel;

    for (const product of products) {
        const filteredProductAttributes = product.AttributeValues?.filter(attributeValue => {
            return (
                checkIfShouldDisplayAsSwatch((attributeValue.Name?.toLocaleLowerCase() ?? '') as DimensionTypes, input.dimensionContext) &&
                context.requestContext.app.config.dimensionsInProductCard.includes(attributeValue.Name?.toLocaleLowerCase())
            );
        });

        if (ArrayExtensions.hasElements(filteredProductAttributes)) {
            for (const attributeValue of filteredProductAttributes) {
                if (attributeValue.Name) {
                    const productionDimensionType: ProductDimensionType =
                        ProductDimensionType[attributeValue.Name as keyof typeof ProductDimensionType];

                    productsDimensionInputs.push(
                        new ProductsDimensionAvailabilitiesInput(product.MasterProductId ?? product.RecordId, productionDimensionType)
                    );
                }
            }
        }
    }

    if (!ArrayExtensions.hasElements(productsDimensionInputs)) {
        return Promise.resolve([]);
    }

    try {
        const disableStockCheckInAppForBackorderableProducts =
            context.requestContext.app.config.disableStockCheckInAppForBackorderableProducts;
        const enableStockCheck = context.requestContext.app.config.enableStockCheck;
        // context.requestContext.apiSettings.oun = '5001';
        const dimensionForProducts: ProductDimensionValueInventoryAvailability[][] = await getDimensionAvailabilitiesForProductsAction(
            productsDimensionInputs,
            context
        );
        const productsDimensionAvailabilities = productsDimensionInputs.map((dimensionInput, index) => {
            let OOSandNBDimensionIDs: string[] = [];
            const dimensionValuesOfProduct = dimensionForProducts[index].map(dimensionValuesWithInventory => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(dimensionValuesWithInventory, inventoryLevel);
                const dimensionValue = dimensionValuesWithInventory.DimensionValue!;
                const NBFlag =
                    dimensionValue.ExtensionProperties &&
                    dimensionValue.ExtensionProperties[1] &&
                    dimensionValue.ExtensionProperties[1].Key === 'IsDimensionAvailableFlag'
                        ? !dimensionValue.ExtensionProperties[1].Value?.BooleanValue
                        : true;
                let isDisable = inventoryLevelCode === InventoryLevelValues.outOfStock;
                if (NBFlag && isDisable) {
                    OOSandNBDimensionIDs = OOSandNBDimensionIDs
                        ? [...OOSandNBDimensionIDs, dimensionValue.RecordId.toString()]
                        : [dimensionValue.RecordId.toString()];
                } else if (enableStockCheck && disableStockCheckInAppForBackorderableProducts && !NBFlag && isDisable) {
                    isDisable = false;
                }
                return {
                    id: dimensionValue.RecordId.toString(),
                    value: dimensionValue.Value ?? '',
                    isDisabled: isDisable,
                    NB: NBFlag,
                    typeCode: dimensionValuesWithInventory.DimensionTypeValue,
                    masterProductId: dimensionInput.masterProductId
                };
            });
            const newDimensionValuesOfProduct = dimensionValuesOfProduct.filter(cDimensions => {
                return !OOSandNBDimensionIDs.includes(cDimensions.id);
            });
            return newDimensionValuesOfProduct;
        });

        if (!ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
            context.telemetry.warning('ProductsDimensionAvailabilities is empty');
            return Promise.resolve([]);
        }
        console.log('Products Dimension Availabilities', productsDimensionAvailabilities);
        return Promise.resolve(productsDimensionAvailabilities);
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- existing error.
        if (typeof error === 'string') {
            context.telemetry.warning(error);
        }
    }

    return Promise.resolve([]);
}

/**
 * The GetDimensionAvailabilitiesForProductSearchResult data action.
 */
export const cgetDimensionAvailabilitiesForProductSearchResultDataAction = createObservableDataAction({
    id: 'CGetDimensionAvailabilitiesForProductSearchResult',
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- existing pattern.
    action: <IAction<ICProductsDimensionsAvailabilities[][]>>cgetDimensionAvailabilitiesForProductSearchResultAction,
    input: createDimensionAvailabilitiesForProductSearchResultInput
});

export default cgetDimensionAvailabilitiesForProductSearchResultDataAction;
